import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import { DataLoaPasca as DataLoaPascaType, DataSiswa } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Loader2,
  Send,
  User,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

// --- API Functions (Sama seperti sebelumnya) ---
const getDataLoa = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/loa-pasca?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data?.data;
};

const ajukanLoa = async ({
  id_data_pendaftaran,
  catatan_peserta,
}: {
  id_data_pendaftaran: string;
  catatan_peserta: string;
}) => {
  const response = await api.post(`/siswa/ajukan-loa-pasca`, {
    id_data_pendaftaran,
    catatan_peserta,
  });
  return response.data;
};

export default function DataLoaPasca({
  dataSiswa,
  idDataPendaftaran,
}: {
  dataSiswa:
    | (DataSiswa & {
        fakultas_nama?: string;
        jurusan_nama?: string;
        prodi_nama?: string;
      })
    | undefined;
  idDataPendaftaran: string | undefined;
}) {
  const [showPengajuan, setShowPengajuan] = useState(false);
  const [form, setForm] = useState({
    catatan_peserta: "",
  });

  const { data, isPending } = useQuery<DataLoaPascaType>({
    queryKey: ["dataLoa", idDataPendaftaran],
    queryFn: () => getDataLoa(idDataPendaftaran || ""),
    enabled: !!idDataPendaftaran,
  });

  const queryClient = useQueryClient();
  const { mutate, isPending: isPendingPengajuan } = useMutation({
    mutationFn: ajukanLoa,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataLoa", idDataPendaftaran],
      });
      setShowPengajuan(false);
      toast.success(data?.message || "Permintaan LoA berhasil diajukan.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message ||
          "Terjadi kesalahan saat mengajukan LoA.",
      );
    },
  });

  const handleAjukanLoa = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate({ ...form, id_data_pendaftaran: idDataPendaftaran || "" });
  };

  // --- Loading Skeleton ---
  if (isPending) {
    return (
      <Card className="overflow-hidden border-slate-200 shadow-sm dark:border-gray-800 dark:bg-gray-950">
        <div className="h-2 bg-slate-100 animate-pulse dark:bg-gray-800" />
        <CardHeader className="gap-2 pb-2">
          <div className="flex justify-between items-center">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-8 w-24 rounded-full" />
          </div>
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-3/4" />
            </div>
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- Helper: Status Configuration ---
  const statusConfig = {
    0: {
      // Pending
      color:
        "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/20 dark:border-amber-900 dark:text-amber-400",
      icon: <Clock className="w-5 h-5 animate-pulse" />,
      title: "Menunggu Persetujuan",
      description: "Permintaan LoA Anda sedang dalam antrean verifikasi.",
      badge: "Pending",
      badgeColor:
        "bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300",
    },
    1: {
      // Approved
      color:
        "bg-green-50 border-green-200 text-green-700 dark:bg-green-950/20 dark:border-green-900 dark:text-green-400",
      icon: <CheckCircle2 className="w-5 h-5" />,
      title: "Permintaan Disetujui",
      description: "Selamat! Permintaan LoA Anda telah disetujui.",
      badge: "Approved",
      badgeColor:
        "bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300",
    },
    2: {
      // Rejected
      color:
        "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/20 dark:border-red-900 dark:text-red-400",
      icon: <XCircle className="w-5 h-5" />,
      title: "Permintaan Ditolak",
      description: "Mohon maaf, permintaan LoA Anda belum dapat disetujui.",
      badge: "Rejected",
      badgeColor:
        "bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300",
    },
  };

  const currentStatus = data ? statusConfig[data.status as 0 | 1 | 2] : null;

  // --- Render: Empty State (Belum Mengajukan) ---
  if (!data) {
    return (
      <>
        <Card className="group relative border-2 border-dashed border-slate-300 bg-slate-50/50 transition-all hover:border-primary/50 hover:bg-slate-50 dark:bg-gray-900/20 dark:border-gray-700 dark:hover:bg-gray-900/40 dark:hover:border-primary/40">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 rounded-full bg-white p-4 shadow-sm ring-1 ring-slate-200 transition-transform group-hover:scale-110 dark:bg-gray-800 dark:ring-gray-700">
              <FileText className="h-10 w-10 text-slate-400 group-hover:text-primary dark:text-slate-500" />
            </div>
            <h3 className="mb-1 text-xl font-semibold text-slate-900 dark:text-gray-100">
              Letter of Acceptance (LoA)
            </h3>
            <p className="mb-6 max-w-sm text-sm text-slate-500 dark:text-slate-400">
              Anda belum mengajukan permintaan LoA. Dokumen ini diperlukan
              sebagai bukti resmi penerimaan mahasiswa pascasarjana.
            </p>
            <Button
              onClick={() => setShowPengajuan(true)}
              className="group-hover:shadow-md transition-all dark:bg-primary dark:text-primary-foreground"
            >
              Ajukan Permintaan LoA <Send className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        <Modal show={showPengajuan} onClose={() => setShowPengajuan(false)}>
          <form onSubmit={handleAjukanLoa}>
            <FormHeader
              title="Pengajuan LoA"
              description="Isi form berikut untuk mengajukan Letter of Acceptance."
              icon={<FileText className="w-5 h-5" />}
            />

            <div className="space-y-5 px-1 py-4">
              {/* Read-only Data Mahasiswa */}
              <div className="rounded-lg bg-slate-50 p-4 border border-slate-100 dark:bg-gray-900 dark:border-gray-800">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-white rounded-full border dark:bg-gray-800 dark:border-gray-700">
                    <User className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold dark:text-slate-400">
                      Pemohon
                    </p>
                    <p className="font-medium text-slate-900 dark:text-gray-100">
                      {dataSiswa?.nama_lengkap}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm border-t border-slate-200 pt-3 dark:border-gray-800">
                  {/* Gunakan min-w-0 agar truncate pada child bisa bekerja di dalam grid */}
                  <div className="min-w-0">
                    <span className="text-slate-500 block text-xs dark:text-slate-400 truncate">
                      Nomor Pendaftaran
                    </span>
                    <span className="font-mono font-medium dark:text-gray-200 block truncate">
                      {idDataPendaftaran}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <span className="text-slate-500 block text-xs dark:text-slate-400 truncate">
                      Program Studi
                    </span>
                    {/* 'truncate' akan memotong teks dengan titik tiga (...) */}
                    {/* 'block' memastikan span mengambil lebar penuh agar pemotongan berfungsi */}
                    <span
                      className="font-medium dark:text-gray-200 block truncate"
                      title={dataSiswa?.prodi_nama || "-"} // Tooltip muncul saat hover jika teks terpotong
                    >
                      {dataSiswa?.prodi_nama || "-"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="catatan"
                  className="text-slate-700 dark:text-gray-300"
                >
                  Catatan Tambahan{" "}
                  <span className="text-slate-400 font-normal dark:text-gray-500">
                    (Opsional)
                  </span>
                </Label>
                <Textarea
                  id="catatan"
                  placeholder="Tuliskan catatan khusus jika diperlukan..."
                  value={form.catatan_peserta}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      catatan_peserta: e.target.value,
                    }))
                  }
                  className="resize-none focus-visible:ring-primary dark:bg-gray-950 dark:border-gray-800"
                  rows={3}
                />
                <p className="text-[10px] text-slate-400 text-right dark:text-slate-500">
                  Maksimal 250 karakter
                </p>
              </div>
            </div>

            <div className="mt-2 flex justify-end gap-3 pt-4 border-t dark:border-gray-800">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPengajuan(false)}
                disabled={isPendingPengajuan}
                className="dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={isPendingPengajuan}
                className="dark:bg-primary dark:text-primary-foreground"
              >
                {isPendingPengajuan ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                    Mengirim...
                  </>
                ) : (
                  <>Kirim Pengajuan</>
                )}
              </Button>
            </div>
          </form>
        </Modal>
      </>
    );
  }

  // --- Render: Existing Data View ---
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-950">
      {/* Header Status Bar */}
      <div
        className={`px-6 py-4 border-b flex items-center justify-between ${currentStatus?.color}`}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/60 rounded-full backdrop-blur-sm border border-black/5 shadow-sm dark:bg-black/20 dark:border-white/10">
            {currentStatus?.icon}
          </div>
          <div>
            <h3 className="font-bold text-sm leading-none mb-1">
              {currentStatus?.title}
            </h3>
            <p className="text-xs opacity-90 font-medium">
              {currentStatus?.description}
            </p>
          </div>
        </div>
        <Badge className={`${currentStatus?.badgeColor} border-0 px-3 py-1`}>
          {currentStatus?.badge}
        </Badge>
      </div>

      <CardContent className="p-6">
        <div className="grid md:grid-cols-12 gap-8">
          {/* Left Column: Student Details */}
          <div className="md:col-span-7 space-y-6">
            <div className="grid grid-cols-2 gap-y-5 gap-x-4">
              <div>
                <Label className="text-slate-500 text-[10px] uppercase tracking-wider font-bold mb-1 block dark:text-slate-400">
                  Nomor Pendaftaran
                </Label>
                <div className="font-mono text-base font-semibold text-slate-900 bg-slate-50 px-2 py-1 rounded w-fit border border-slate-100 dark:bg-gray-900 dark:border-gray-800 dark:text-gray-100">
                  {dataSiswa?.data_pendaftaran &&
                    dataSiswa?.data_pendaftaran[0]?.nomor_pendaftaran}
                </div>
              </div>
              <div>
                <Label className="text-slate-500 text-[10px] uppercase tracking-wider font-bold mb-1 block dark:text-slate-400">
                  Nama Lengkap
                </Label>
                <div className="text-base font-medium text-slate-900 dark:text-gray-100">
                  {dataSiswa?.nama_lengkap}
                </div>
              </div>
            </div>

            <Separator className="dark:bg-gray-800" />

            {/* User Note Section */}
            <div>
              <Label className="text-slate-500 text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-2 dark:text-slate-400">
                <FileText className="w-3 h-3" /> Catatan Pengajuan Anda
              </Label>
              {form.catatan_peserta || data.catatan_peserta ? ( // Assuming API returns 'catatan_peserta'
                <div className="bg-slate-50 p-3 rounded-md border border-slate-100 text-sm text-slate-600 italic relative dark:bg-gray-900 dark:border-gray-800 dark:text-gray-300">
                  <span className="text-slate-300 absolute top-2 left-2 text-2xl font-serif dark:text-gray-700">
                    “
                  </span>
                  <p className="px-4 z-10 relative">
                    {data.catatan_peserta || form.catatan_peserta}
                  </p>
                </div>
              ) : (
                <span className="text-sm text-slate-400 italic dark:text-slate-500">
                  Tidak ada catatan tambahan.
                </span>
              )}
            </div>
          </div>

          {/* Right Column: Feedback & Actions */}
          <div className="md:col-span-5 flex flex-col justify-center border-l pl-0 md:pl-8 border-slate-100 border-t md:border-t-0 pt-6 md:pt-0 dark:border-gray-800">
            {/* REJECTED STATE */}
            {data.status === 2 && (
              <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="p-4 rounded-lg bg-red-50 border border-red-100 text-red-800 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
                  <div className="flex items-center gap-2 mb-2 font-bold text-sm">
                    <AlertCircle className="w-4 h-4" /> Catatan Verifikator
                  </div>
                  <p className="text-sm leading-relaxed">
                    {data.catatan_operator ||
                      "Mohon hubungi admin untuk informasi lebih lanjut mengenai penolakan ini."}
                  </p>
                </div>
                <div className="text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-slate-500 dark:border-gray-700 dark:text-gray-400"
                    disabled
                  >
                    Tidak dapat diunduh
                  </Button>
                </div>
              </div>
            )}

            {/* APPROVED STATE */}
            {data.status === 1 && data.file_loa && (
              <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="bg-white rounded-xl border-2 border-slate-100 p-4 shadow-sm hover:border-green-200 hover:shadow-md transition-all group dark:bg-gray-900 dark:border-gray-800 dark:hover:border-green-900">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-100 transition-colors dark:bg-red-900/20 dark:group-hover:bg-red-900/30">
                      <FileText className="w-6 h-6 text-red-500 dark:text-red-400" />
                    </div>
                    <Badge
                      variant="outline"
                      className="text-[10px] dark:border-gray-700 dark:text-gray-300"
                    >
                      PDF
                    </Badge>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm dark:text-gray-100">
                      Dokumen LoA Resmi
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 dark:text-gray-400">
                      Siap untuk diunduh dan dicetak.
                    </p>
                  </div>
                </div>

                <Button
                  asChild
                  className="w-full bg-slate-900 hover:bg-slate-800 shadow-lg shadow-slate-200 dark:bg-primary dark:text-primary-foreground dark:shadow-none"
                >
                  <Link to={data.file_loa} target="_blank">
                    <Download className="mr-2 h-4 w-4" /> Unduh Dokumen
                  </Link>
                </Button>
              </div>
            )}

            {/* PENDING STATE */}
            {data.status === 0 && (
              <div className="flex flex-col items-center justify-center text-center space-y-3 py-4 animate-in fade-in zoom-in-95 duration-500">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center dark:bg-amber-900/20">
                  <Clock className="w-6 h-6 text-amber-500 animate-[spin_10s_linear_infinite]" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-slate-900 dark:text-gray-100">
                    Sedang Diproses
                  </p>
                  <p className="text-xs text-slate-500 max-w-[200px] mx-auto dark:text-gray-400">
                    Tim akademik kami sedang memverifikasi data Anda. Notifikasi
                    akan muncul di sini setelah selesai.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
